import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { confirm } from './ConfirmDialog';
import SearchSelect from './SearchSelect';
import { getAssetBaseUrl } from '../utils/assetBase';

const defaultDays = {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true
};

const emptyForm = {
    name: '',
    price: '',
    active: true,
    available_everyday: true,
    always_time_active: false,
    start_time: '11:00',
    end_time: '17:00',
    items: [],
    ...defaultDays
};

const dayLabels = [
    { key: 'monday', label: 'B.e', fullLabel: 'Bazar ertəsi' },
    { key: 'tuesday', label: 'Ç.a', fullLabel: 'Çərşənbə axşamı' },
    { key: 'wednesday', label: 'Ç', fullLabel: 'Çərşənbə' },
    { key: 'thursday', label: 'C.a', fullLabel: 'Cümə axşamı' },
    { key: 'friday', label: 'C', fullLabel: 'Cümə' },
    { key: 'saturday', label: 'Ş', fullLabel: 'Şənbə' },
    { key: 'sunday', label: 'B', fullLabel: 'Bazar' }
];

const trimQty = (value, digits = 3) => Number(value || 0).toFixed(digits).replace(/\.?0+$/, '');

const formatSaleQty = (qty, saleType) => {
    if ((saleType || 'piece') === 'piece') {
        return `${trimQty(qty)}əd`;  // onluq porsiya (məs. 1.333) da göstərilsin
    }

    if (saleType === 'weight') {
        return `${trimQty(qty)}kq`;
    }

    if (saleType === 'liter') {
        return `${trimQty(qty)}lt`;
    }

    return trimQty(qty);
};

const hourOptions = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const minuteOptions = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'));

const splitTime = (value, fallback = '11:00') => {
    const safeValue = String(value || fallback);
    const [hour = '11', minute = '00'] = safeValue.split(':');
    return {
        hour: hour.padStart(2, '0'),
        minute: minute.padStart(2, '0')
    };
};

const MenuSetManager = ({ compact = false }) => {
    const [sets, setSets] = useState([]);
    const [compDragIdx, setCompDragIdx] = useState(null); // set komponent drag-drop indeksi
    const [setDragIdx, setSetDragIdx] = useState(null);    // set siyahısı drag-drop indeksi
    const [expandedSetId, setExpandedSetId] = useState(null); // açıq set (akkordeon)
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingSet, setEditingSet] = useState(null);
    const [form, setForm] = useState(emptyForm);
    // Set-ə məhsul axtarış + Enter ilə əlavə etmə
    const [itemSearch, setItemSearch] = useState('');
    const [itemHighlight, setItemHighlight] = useState(-1);
    const itemSearchRef = useRef(null);
    const [setsCategoryImage, setSetsCategoryImage] = useState(null);
    const [uploadingCatImage, setUploadingCatImage] = useState(false);
    const [uploadingSetId, setUploadingSetId] = useState(null);
    // ── WhatsApp göndər (setlər — texkarta ilə eyni popup) ──
    const [waSet, setWaSet] = useState(null);           // hazırda göndərilən set (null = bağlı)
    const [waNum, setWaNum] = useState('');
    const [waWithPrices, setWaWithPrices] = useState(false);
    const [waSending, setWaSending] = useState(false);
    const [waHistory, setWaHistory] = useState([]); // tenant üzrə serverdən

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [setsRes, itemsRes, catImgRes] = await Promise.all([
                api.get('/menu/sets/all'),
                api.get('/menu/items/all'),
                api.get('/menu/sets-category/image').catch(() => ({ data: { image_url: null } }))
            ]);
            setSetsCategoryImage(catImgRes.data?.image_url || null);
            // Backend-dən gələn sıra (sort_order) qorunur — admin özü idarə edir
            setSets(setsRes.data.data || []);
            setMenuItems(itemsRes.data.data || []);
            // Son göndərilən WhatsApp nömrələri — tenant üzrə (serverdən)
            api.get('/recipes/wa-recent').then((r) => setWaHistory(r.data?.numbers || [])).catch(() => {});
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Menu setləri yüklənmədi.');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditingSet(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = (menuSet) => {
        const nextItems = (menuSet.items || []).map((item) => {
            const mode = item.unit_mode === 'pors' ? 'pors' : 'eded';
            const st = item.sale_type || 'piece';
            // Saxlanan qty PORSIYA-dır. 'eded' rejimində istifadəçiyə ƏDƏD göstər (qty×porsiyadakı ədəd)
            let dispQty = String(item.qty);
            if (st === 'piece' && mode === 'eded') {
                const pp = Math.max(1, parseInt(item.portion_pieces, 10) || 1);
                dispQty = String(Math.round(Number(item.qty || 0) * pp));
            }
            return { menu_item_id: item.menu_item_id, qty: dispQty, unit_mode: mode };
        });

        setEditingSet(menuSet);
        setForm({
            name: menuSet.name || '',
            price: String(menuSet.price ?? ''),
            active: !!menuSet.active,
            available_everyday: !!menuSet.available_everyday,
            always_time_active: !menuSet.start_time || !menuSet.end_time,
            start_time: menuSet.start_time ? String(menuSet.start_time).slice(0, 5) : '11:00',
            end_time: menuSet.end_time ? String(menuSet.end_time).slice(0, 5) : '17:00',
            monday: !!menuSet.monday,
            tuesday: !!menuSet.tuesday,
            wednesday: !!menuSet.wednesday,
            thursday: !!menuSet.thursday,
            friday: !!menuSet.friday,
            saturday: !!menuSet.saturday,
            sunday: !!menuSet.sunday,
            items: nextItems.length > 0 ? nextItems : [{ menu_item_id: '', qty: '1', unit_mode: 'eded' }]
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingSet(null);
        setForm(emptyForm);
        setItemSearch('');
        setItemHighlight(-1);
    };

    const addItemRow = () => {
        setForm((prev) => ({
            ...prev,
            items: [...prev.items, { menu_item_id: '', qty: '1', unit_mode: 'eded' }]
        }));
    };

    // 'eded' ↔ 'pors' keçidi — rəqəmi vahidə uyğun çevir ki, mənası qorunsun
    const toggleUnitMode = (index) => {
        setForm((prev) => {
            const items = [...prev.items];
            const it = items[index];
            const mi = getItemById(it.menu_item_id);
            const pp = Math.max(1, parseInt(mi?.portion_pieces, 10) || 1);
            const cur = parseFloat(it.qty || 0) || 0;
            if (it.unit_mode === 'pors') {
                items[index] = { ...it, unit_mode: 'eded', qty: String(Math.round(cur * pp)) };
            } else {
                items[index] = { ...it, unit_mode: 'pors', qty: String(Number((cur / pp).toFixed(3))) };
            }
            return { ...prev, items };
        });
    };

    // Axtarışdan məhsulu set-ə əlavə et: artıq varsa miqdarı artır, yoxsa boş sətri doldur/yeni sətir
    const addItemBySearch = (menuItem) => {
        if (!menuItem) return;
        setForm((prev) => {
            const items = [...prev.items];
            const existIdx = items.findIndex((it) => String(it.menu_item_id) === String(menuItem.id));
            if (existIdx >= 0) {
                const st = menuItem.sale_type || 'piece';
                const cur = Number(items[existIdx].qty) || 0;
                items[existIdx] = {
                    ...items[existIdx],
                    qty: st === 'piece' ? String(Math.round(cur) + 1) : String(cur || 1)
                };
            } else {
                const emptyIdx = items.findIndex((it) => !it.menu_item_id);
                const row = { menu_item_id: String(menuItem.id), qty: '1', unit_mode: 'eded' };
                if (emptyIdx >= 0) items[emptyIdx] = row;
                else items.push(row);
            }
            return { ...prev, items };
        });
        setItemSearch('');
        setItemHighlight(-1);
        setTimeout(() => itemSearchRef.current?.focus(), 40);
    };

    const updateItemRow = (index, patch) => {
        setForm((prev) => ({
            ...prev,
            items: prev.items.map((item, itemIndex) => (
                itemIndex === index ? { ...item, ...patch } : item
            ))
        }));
    };

    const removeItemRow = (index) => {
        setForm((prev) => ({
            ...prev,
            items: prev.items.filter((_, itemIndex) => itemIndex !== index)
        }));
    };

    // Set-lərin sırasını drag-and-drop ilə dəyiş — QR menyuda da həmin sıra ilə görünür
    const reorderSets = async (from, to) => {
        if (from == null || from === to) return;
        const next = [...sets];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setSets(next); // optimistik
        try {
            await api.post('/menu/sets/reorder', { ids: next.map((s) => s.id) });
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Sıra saxlanmadı.');
            loadData();
        }
    };

    // Ədəd məhsulda göstərilən miqdar: qty (porsiya) × portion_pieces = real ədəd.
    // Məs. 1.333 porsiya × 3 = 4 ədəd. QR-da "1.330" yerinə "4 əd" görünür.
    const fmtComponentQty = (menuItemId, qty, unitMode) => {
        const mi = getItemById(menuItemId);
        if ((mi?.sale_type || 'piece') === 'piece') {
            if (String(unitMode || 'eded') === 'pors') {
                return `${trimQty(qty)} pors`;
            }
            const pp = Math.max(1, parseInt(mi?.portion_pieces, 10) || 1);
            return `${Math.round((Number(qty) || 0) * pp)}əd`;
        }
        return formatSaleQty(qty, mi?.sale_type);
    };

    // Drag-and-drop ilə komponentləri sırala
    const reorderItemRow = (from, to) => {
        if (from == null || from === to) return;
        setForm((prev) => {
            const items = [...prev.items];
            const [moved] = items.splice(from, 1);
            items.splice(to, 0, moved);
            return { ...prev, items };
        });
    };

    // Komponentin sırasını dəyiş (▲/▼) — bu sıra QR menyuda da eyni görünəcək
    const moveItemRow = (index, dir) => {
        setForm((prev) => {
            const items = [...prev.items];
            const j = index + dir;
            if (j < 0 || j >= items.length) return prev;
            [items[index], items[j]] = [items[j], items[index]];
            return { ...prev, items };
        });
    };

    const updateTimeField = (field, part, value) => {
        setForm((prev) => {
            const current = splitTime(prev[field], field === 'start_time' ? '11:00' : '17:00');
            const next = {
                ...current,
                [part]: String(value).padStart(2, '0')
            };

            return {
                ...prev,
                [field]: `${next.hour}:${next.minute}`
            };
        });
    };

    const submit = async (e) => {
        e.preventDefault();

        const payload = {
            ...form,
            price: parseFloat(form.price || 0),
            start_time: form.always_time_active ? null : form.start_time,
            end_time: form.always_time_active ? null : form.end_time,
            items: form.items
                .map((item) => {
                    const mi = getItemById(item.menu_item_id);
                    const st = mi?.sale_type || 'piece';
                    const mode = item.unit_mode === 'pors' ? 'pors' : 'eded';
                    let porsiya = parseFloat(item.qty || 0);
                    // 'eded' rejimi: istifadəçi ƏDƏD yazır → porsiya = ədəd / porsiyadakı ədəd
                    // (anbar/texkart porsiya ilə işləyir, ona görə saxlanan qty porsiyadır)
                    if (st === 'piece' && mode === 'eded') {
                        const pp = Math.max(1, parseInt(mi?.portion_pieces, 10) || 1);
                        porsiya = parseFloat(item.qty || 0) / pp;
                    }
                    return { menu_item_id: Number(item.menu_item_id), qty: porsiya, unit_mode: mode };
                })
                .filter((item) => item.menu_item_id > 0 && item.qty > 0)
        };

        if (!payload.name.trim()) {
            toast.error('Set adı daxil edin.');
            return;
        }

        if (payload.items.length === 0) {
            toast.error('Set daxilinə ən azı 1 məhsul əlavə edin.');
            return;
        }

        if (!payload.available_everyday && !dayLabels.some((day) => payload[day.key])) {
            toast.error('Ən azı 1 gün seçin.');
            return;
        }

        try {
            if (editingSet) {
                await api.put(`/menu/sets/${editingSet.id}`, payload);
                toast.success('Menu seti yeniləndi.');
            } else {
                await api.post('/menu/sets', payload);
                toast.success('Menu seti əlavə edildi.');
            }

            closeModal();
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const toggleSet = async (menuSet) => {
        try {
            await api.put(`/menu/sets/${menuSet.id}`, {
                ...menuSet,
                active: !menuSet.active,
                items: (menuSet.items || []).map((item) => ({
                    menu_item_id: item.menu_item_id,
                    qty: item.qty
                }))
            });
            toast.success(menuSet.active ? 'Deaktiv edildi.' : 'Aktiv edildi.');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const deleteSet = async (menuSet) => {
        if (!(await confirm(`"${menuSet.name}" silinsin?`))) {
            return;
        }

        try {
            await api.delete(`/menu/sets/${menuSet.id}`);
            toast.success('Menu seti silindi.');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinə bilmədi.');
        }
    };

    const uploadCategoryImage = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append('image', file, file.name);
        setUploadingCatImage(true);
        try {
            const res = await api.post('/menu/sets-category/image', fd);
            setSetsCategoryImage(res.data?.image_url || null);
            toast.success('Şəkil yükləndi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yüklənmədi.');
        } finally {
            setUploadingCatImage(false);
        }
    };

    const deleteCategoryImage = async () => {
        if (!(await confirm('Setlər kateqoriya şəkli silinsin?'))) return;
        try {
            await api.delete('/menu/sets-category/image');
            setSetsCategoryImage(null);
            toast.success('Silindi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        }
    };

    const uploadSetImage = async (setId, file) => {
        if (!file || !setId) return;
        const fd = new FormData();
        fd.append('image', file, file.name);
        setUploadingSetId(setId);
        try {
            await api.post(`/menu/sets/${setId}/image`, fd);
            toast.success('Şəkil yükləndi.');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yüklənmədi.');
        } finally {
            setUploadingSetId(null);
        }
    };

    const deleteSetImage = async (setId) => {
        if (!(await confirm('Set şəkli silinsin?'))) return;
        try {
            await api.delete(`/menu/sets/${setId}/image`);
            toast.success('Silindi.');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        }
    };

    // ── WhatsApp: set mesajı + göndər (texkarta ilə eyni axın) ──
    const setImageUrl = (menuSet) => {
        const raw = menuSet?.image_url || '';
        if (!raw) return '';
        return raw.startsWith('http') ? raw : `${getAssetBaseUrl()}${raw}`;
    };

    const buildSetMessage = (menuSet, withPrices) => {
        if (!menuSet) return '';
        const items = menuSet.items || [];
        let totalMaya = 0, totalSale = 0;
        const lines = items.map((item) => {
            const mi = getItemById(item.menu_item_id);
            const qty = Number(item.qty) || 0;
            const maya = mi ? (Number(mi.recipe_cost) > 0 ? Number(mi.recipe_cost) : Number(mi.cost_price || 0)) : 0;
            const price = mi ? Number(mi.price || 0) : 0;
            totalMaya += qty * maya;
            totalSale += qty * price;
            const label = `${fmtComponentQty(item.menu_item_id, item.qty, item.unit_mode)} ${item.menu_item_name}`;
            if (withPrices) {
                return `${label} — alış ${(qty * maya).toFixed(2)} ₼ · satış ${(qty * price).toFixed(2)} ₼`;
            }
            return label;
        });
        let msg = `🎁 ${menuSet.name}\n\n${lines.join('\n')}`;
        if (withPrices) {
            msg += `\n\nCəmi maya: ${totalMaya.toFixed(2)} azn`;
            msg += `\nAyrı-ayrı satış: ${totalSale.toFixed(2)} azn`;
        }
        msg += `\nSet qiyməti: ${Number(menuSet.price || 0).toFixed(2)} azn`;
        return msg;
    };

    const openSetWhatsapp = (menuSet) => { setWaSet(menuSet); setWaNum(''); setWaWithPrices(false); };

    const sendSetWhatsapp = async () => {
        const num = String(waNum).trim();
        if (!num) { toast.error('Nömrə yazın.'); return; }
        const message = buildSetMessage(waSet, waWithPrices);
        const imageUrl = setImageUrl(waSet);
        setWaSending(true);
        try {
            const res = await api.post('/recipes/whatsapp-send', { numbers: [num], message, imageUrl });
            if (res.data?.warn) toast(res.data.msg, { icon: '⚠️', duration: 9000 });
            else toast.success(res.data?.msg || 'WhatsApp-a göndərildi.');
            // Tenant üzrə server siyahısı (bütün cihazlarda ortaq)
            if (Array.isArray(res.data?.recent)) setWaHistory(res.data.recent);
            setWaSet(null);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Göndərilmədi.');
        } finally { setWaSending(false); }
    };

    const availableItemOptions = useMemo(
        () => menuItems.filter((item) => item.active),
        [menuItems]
    );

    // Axtarışlı seçim üçün: ad + normal satış qiyməti (set redaktəsində qabaqda görünür)
    const itemSelectOptions = useMemo(
        () => availableItemOptions.map((mi) => ({
            value: mi.id,
            label: `${mi.name} — ${Number(mi.price || 0).toFixed(2)} ₼`,
        })),
        [availableItemOptions]
    );

    const getItemById = (itemId) => menuItems.find((item) => item.id === Number(itemId));

    return (
        <>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h3 className="font-bold text-gray-800">Menu setləri</h3>
                        <p className="text-xs text-gray-400 mt-1">
                            Çay seti kimi kombolar yaradın, gün və saat seçin.
                        </p>
                    </div>
                    <button
                        onClick={openCreate}
                        className="px-4 py-2 rounded-xl text-sm font-bold text-white transition-all"
                        style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                    >
                        + Set əlavə et
                    </button>
                </div>

                {/* "Setlər" kateqoriya şəkli — QR menyuda kateqoriya kartında görünür */}
                <div className="px-5 py-4 border-b border-gray-100 bg-purple-50/30 flex items-center gap-4 flex-wrap">
                    <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl border border-purple-200 bg-white flex items-center justify-center">
                        {setsCategoryImage ? (
                            <img src={setsCategoryImage} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                            <span className="text-2xl text-purple-300">🎁</span>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-purple-900">"Setlər" kateqoriya şəkli</div>
                        <div className="text-[11px] text-purple-700/80 mt-0.5">QR menyuda və ofisiant ekranında setlər kateqoriyası üçün göstərilən şəkil</div>
                    </div>
                    <div className="flex gap-2">
                        <label className={`inline-flex cursor-pointer items-center justify-center rounded-lg px-3 py-1.5 text-xs font-semibold text-white ${uploadingCatImage ? 'bg-gray-300 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}>
                            {uploadingCatImage ? 'Yüklənir...' : (setsCategoryImage ? 'Dəyiş' : 'Şəkil yüklə')}
                            <input type="file" accept="image/*" className="hidden" disabled={uploadingCatImage}
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = '';
                                    if (f) uploadCategoryImage(f);
                                }} />
                        </label>
                        {setsCategoryImage && (
                            <button type="button" onClick={deleteCategoryImage}
                                className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50">
                                Sil
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="p-8 text-center text-gray-400">Yüklənir...</div>
                ) : sets.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">Hələ menu seti yoxdur.</div>
                ) : (
                    <div className="flex flex-col gap-2 p-4">
                        {sets.map((menuSet, index) => {
                            const open = expandedSetId === menuSet.id;
                            return (
                            <div key={menuSet.id}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => { reorderSets(setDragIdx, index); setSetDragIdx(null); }}
                                className={`rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden transition-all ${setDragIdx === index ? 'opacity-40 ring-2 ring-primary-300' : ''}`}>
                                {/* Başlıq — klik aç/bağla, ⠿ ilə sürüşdür */}
                                <div className="flex items-center gap-2 p-2.5">
                                    <div draggable
                                        onDragStart={() => setSetDragIdx(index)}
                                        onDragEnd={() => setSetDragIdx(null)}
                                        title="Sürüşdürüb sırala"
                                        className="flex h-9 w-6 flex-shrink-0 cursor-move items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 select-none">⠿</div>
                                    <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-white flex items-center justify-center">
                                        {menuSet.image_url ? (
                                            <img src={menuSet.image_url} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                        ) : (
                                            <span className="text-lg text-gray-300">🎁</span>
                                        )}
                                    </div>
                                    <button type="button" onClick={() => setExpandedSetId(open ? null : menuSet.id)}
                                        className="flex flex-1 min-w-0 items-center gap-2 text-left">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-gray-800 truncate text-sm">{menuSet.name}</div>
                                            <div className="text-xs font-mono text-primary-600">{parseFloat(menuSet.price || 0).toFixed(2)} ₼</div>
                                        </div>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                                            menuSet.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'
                                        }`}>
                                            {menuSet.active ? 'Aktiv' : 'Passiv'}
                                        </span>
                                        <span className={`flex-shrink-0 text-gray-400 text-xs transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
                                    </button>
                                </div>

                                {open && (
                                <div className="border-t border-gray-100 px-3 pb-3 pt-2">
                                <div className="text-xs text-gray-500">
                                    {(menuSet.available_everyday
                                        ? 'Hər gün'
                                        : dayLabels.filter((day) => menuSet[day.key]).map((day) => day.fullLabel).join(', ')
                                    ) || 'Gün seçilməyib'}
                                    {' · '}
                                    {menuSet.start_time ? String(menuSet.start_time).slice(0, 5) : '--:--'}
                                    {' - '}
                                    {menuSet.end_time ? String(menuSet.end_time).slice(0, 5) : '--:--'}
                                </div>

                                {/* ── Maya / qiymət bölgüsü (admin) ── */}
                                {(() => {
                                    const items = menuSet.items || [];
                                    let totalMaya = 0, totalSeparate = 0;
                                    const rows = items.map((item) => {
                                        const mi = getItemById(item.menu_item_id);
                                        const qty = Number(item.qty) || 0;
                                        const maya = mi ? (Number(mi.recipe_cost) > 0 ? Number(mi.recipe_cost) : Number(mi.cost_price || 0)) : 0;
                                        const price = mi ? Number(mi.price || 0) : 0;
                                        totalMaya += qty * maya;
                                        totalSeparate += qty * price;
                                        return { item, qty, lineMaya: qty * maya, hasMaya: maya > 0, lineSale: qty * price, hasSale: price > 0 };
                                    });
                                    const setPrice = Number(menuSet.price || 0);
                                    const profit = setPrice - totalMaya;
                                    return (
                                        <div className="mt-3 rounded-xl border border-amber-100 bg-amber-50/40 p-2.5 text-xs">
                                            {rows.map(({ item, lineMaya, hasMaya, lineSale, hasSale }) => (
                                                <div key={item.id} className="flex items-baseline gap-1 py-0.5 text-gray-600">
                                                    <span className="whitespace-nowrap">{fmtComponentQty(item.menu_item_id, item.qty, item.unit_mode)} {item.menu_item_name}</span>
                                                    <span className="flex-1 self-center min-w-[16px] border-b border-dotted border-gray-300"></span>
                                                    <span className="font-mono whitespace-nowrap text-rose-600" title="Alış (maya)">
                                                        {hasMaya ? `${lineMaya.toFixed(2)} ₼` : '—'}
                                                    </span>
                                                    <span className="font-mono whitespace-nowrap text-emerald-600 w-[72px] text-right" title="Ayrı satış qiyməti">
                                                        {hasSale ? `${lineSale.toFixed(2)} ₼` : '—'}
                                                    </span>
                                                </div>
                                            ))}
                                            <div className="mt-1.5 border-t border-amber-200 pt-1.5 space-y-0.5">
                                                <div className="flex justify-between font-semibold text-rose-700">
                                                    <span>Cəmi maya dəyəri</span><span className="font-mono">{totalMaya.toFixed(2)} ₼</span>
                                                </div>
                                                <div className="flex justify-between text-gray-500">
                                                    <span>Ayrı-ayrı satış (normalda)</span><span className="font-mono">{totalSeparate.toFixed(2)} ₼</span>
                                                </div>
                                                <div className="flex justify-between font-semibold text-emerald-700">
                                                    <span>Set qiyməti</span><span className="font-mono">{setPrice.toFixed(2)} ₼</span>
                                                </div>
                                                <div className="flex justify-between font-bold text-primary-700">
                                                    <span>Qazanc (set − maya)</span>
                                                    <span className="font-mono">{profit.toFixed(2)} ₼{setPrice > 0 ? ` (${Math.round((profit / setPrice) * 100)}%)` : ''}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                <div className="mt-4 flex gap-2 flex-wrap">
                                    <button
                                        onClick={() => openEdit(menuSet)}
                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                                    >
                                        Redaktə et
                                    </button>
                                    <label className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer inline-flex items-center transition-all ${
                                        uploadingSetId === menuSet.id ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                                    }`}>
                                        {uploadingSetId === menuSet.id ? 'Yüklənir...' : (menuSet.image_url ? '🖼️ Şəkli dəyiş' : '🖼️ Şəkil yüklə')}
                                        <input type="file" accept="image/*" className="hidden"
                                            disabled={uploadingSetId === menuSet.id}
                                            onChange={(e) => {
                                                const f = e.target.files?.[0];
                                                e.target.value = '';
                                                if (f) uploadSetImage(menuSet.id, f);
                                            }} />
                                    </label>
                                    {menuSet.image_url && (
                                        <button onClick={() => deleteSetImage(menuSet.id)}
                                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-500 hover:bg-red-100 transition-all">
                                            Şəkli sil
                                        </button>
                                    )}
                                    <button
                                        onClick={() => openSetWhatsapp(menuSet)}
                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-50 text-green-700 hover:bg-green-100 transition-all"
                                    >
                                        📱 WhatsApp
                                    </button>
                                    <button
                                        onClick={() => toggleSet(menuSet)}
                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 transition-all"
                                    >
                                        {menuSet.active ? 'Deaktiv et' : 'Aktiv et'}
                                    </button>
                                    <button
                                        onClick={() => deleteSet(menuSet)}
                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-500 hover:bg-red-100 transition-all"
                                    >
                                        Sil
                                    </button>
                                </div>
                                </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {showModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && closeModal()}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-3xl shadow-xl max-h-[90vh] overflow-y-auto">
                        <h3 className="font-bold text-lg mb-5 text-gray-800">
                            {editingSet ? 'Menu setini redaktə et' : 'Yeni menu seti'}
                        </h3>

                        <form onSubmit={submit} className="flex flex-col gap-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Set adı
                                    </label>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                                        placeholder="Məs: Qarışıq set"
                                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Set qiyməti
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form.price}
                                        onChange={(e) => setForm((prev) => ({ ...prev, price: e.target.value }))}
                                        placeholder="0.00"
                                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="rounded-xl border border-gray-200 p-4">
                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                                        <input
                                            type="checkbox"
                                            checked={form.available_everyday}
                                            onChange={(e) => setForm((prev) => ({ ...prev, available_everyday: e.target.checked }))}
                                        />
                                        Hər gün aktiv olsun
                                    </label>
                                    <p className="text-xs text-gray-400 mb-3">
                                        Söndürsəniz, aşağıdan yalnız aktiv olacaq günləri seçə bilərsiniz.
                                    </p>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2">
                                        {dayLabels.map((day) => {
                                            const selected = !!form[day.key];
                                            const disabled = form.available_everyday;

                                            return (
                                                <button
                                                    key={day.key}
                                                    type="button"
                                                    title={day.fullLabel}
                                                    disabled={disabled}
                                                    onClick={() => setForm((prev) => ({ ...prev, [day.key]: !prev[day.key] }))}
                                                    className={`rounded-lg border px-2 py-2 text-center text-xs font-semibold transition-all ${
                                                        disabled
                                                            ? 'bg-gray-100 text-gray-400 border-gray-100 cursor-not-allowed'
                                                            : selected
                                                                ? 'bg-green-100 text-green-700 border-green-300'
                                                                : 'border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-700'
                                                    }`}
                                                >
                                                    {day.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="rounded-xl border border-gray-200 p-4">
                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                                        <input
                                            type="checkbox"
                                            checked={form.active}
                                            onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
                                        />
                                        Aktiv olsun
                                    </label>
                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                                        <input
                                            type="checkbox"
                                            checked={form.always_time_active}
                                            onChange={(e) => setForm((prev) => ({ ...prev, always_time_active: e.target.checked }))}
                                        />
                                        Hər zaman aktiv olsun
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                                Başlanğıc
                                            </div>
                                            <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
                                                <select
                                                    value={splitTime(form.start_time, '11:00').hour}
                                                    onChange={(e) => updateTimeField('start_time', 'hour', e.target.value)}
                                                    disabled={form.always_time_active}
                                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all disabled:bg-gray-100 disabled:text-gray-400"
                                                >
                                                    {hourOptions.map((hour) => (
                                                        <option key={hour} value={hour}>{hour}</option>
                                                    ))}
                                                </select>
                                                <div className="flex items-center justify-center text-sm font-bold text-gray-400">:</div>
                                                <select
                                                    value={splitTime(form.start_time, '11:00').minute}
                                                    onChange={(e) => updateTimeField('start_time', 'minute', e.target.value)}
                                                    disabled={form.always_time_active}
                                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all disabled:bg-gray-100 disabled:text-gray-400"
                                                >
                                                    {minuteOptions.map((minute) => (
                                                        <option key={minute} value={minute}>{minute}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                                Bitiş
                                            </div>
                                            <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
                                                <select
                                                    value={splitTime(form.end_time, '17:00').hour}
                                                    onChange={(e) => updateTimeField('end_time', 'hour', e.target.value)}
                                                    disabled={form.always_time_active}
                                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all disabled:bg-gray-100 disabled:text-gray-400"
                                                >
                                                    {hourOptions.map((hour) => (
                                                        <option key={hour} value={hour}>{hour}</option>
                                                    ))}
                                                </select>
                                                <div className="flex items-center justify-center text-sm font-bold text-gray-400">:</div>
                                                <select
                                                    value={splitTime(form.end_time, '17:00').minute}
                                                    onChange={(e) => updateTimeField('end_time', 'minute', e.target.value)}
                                                    disabled={form.always_time_active}
                                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all disabled:bg-gray-100 disabled:text-gray-400"
                                                >
                                                    {minuteOptions.map((minute) => (
                                                        <option key={minute} value={minute}>{minute}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-400 mt-3">
                                        Saat seçimi 24 saat rejimindədir. Məsələn: 17:00
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-xl border border-gray-200 p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="text-sm font-semibold text-gray-700">Set daxilindəki məhsullar</div>
                                    <button
                                        type="button"
                                        onClick={addItemRow}
                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-50 text-primary-700 hover:bg-primary-100 transition-all"
                                    >
                                        + Boş sətir
                                    </button>
                                </div>

                                {/* Axtarış + Enter ilə məhsul əlavə et */}
                                {(() => {
                                    const q = itemSearch.trim().toLowerCase();
                                    const suggestions = q
                                        ? availableItemOptions.filter((mi) => mi.name.toLowerCase().includes(q)).slice(0, 30)
                                        : [];
                                    return (
                                        <div className="relative mb-3">
                                            <input
                                                ref={itemSearchRef}
                                                type="text"
                                                value={itemSearch}
                                                onChange={(e) => { setItemSearch(e.target.value); setItemHighlight(-1); }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'ArrowDown') {
                                                        e.preventDefault();
                                                        setItemHighlight((h) => Math.min((h < 0 ? -1 : h) + 1, suggestions.length - 1));
                                                    } else if (e.key === 'ArrowUp') {
                                                        e.preventDefault();
                                                        setItemHighlight((h) => Math.max(h - 1, 0));
                                                    } else if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        if (suggestions.length === 1) addItemBySearch(suggestions[0]);
                                                        else if (itemHighlight >= 0 && itemHighlight < suggestions.length) addItemBySearch(suggestions[itemHighlight]);
                                                        else if (suggestions.length > 1) setItemHighlight(0);
                                                    } else if (e.key === 'Escape') {
                                                        setItemSearch(''); setItemHighlight(-1);
                                                    }
                                                }}
                                                placeholder="🔍 Məhsul axtar, Enter ilə set-ə əlavə et..."
                                                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all"
                                            />
                                            {suggestions.length > 0 && (
                                                <div className="absolute z-30 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg">
                                                    {suggestions.map((mi, idx) => {
                                                        const inSet = form.items.some((it) => String(it.menu_item_id) === String(mi.id));
                                                        return (
                                                            <div
                                                                key={mi.id}
                                                                onClick={() => addItemBySearch(mi)}
                                                                className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between ${
                                                                    idx === itemHighlight ? 'bg-primary-100 text-primary-800' : 'hover:bg-gray-50 text-gray-700'
                                                                }`}
                                                            >
                                                                <span className="truncate">{mi.name}</span>
                                                                {inSet && <span className="text-[10px] text-green-600 font-semibold ml-2 shrink-0">setdə var ✓</span>}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                <div className="flex flex-col gap-3">
                                    {form.items.map((item, index) => {
                                        const selectedItem = getItemById(item.menu_item_id);
                                        const saleType = selectedItem?.sale_type || 'piece';

                                        return (
                                            <div key={index}
                                                onDragOver={(e) => { e.preventDefault(); }}
                                                onDrop={() => { reorderItemRow(compDragIdx, index); setCompDragIdx(null); }}
                                                className={`grid grid-cols-12 gap-2 items-center rounded-xl transition-all ${compDragIdx === index ? 'opacity-40' : ''}`}>
                                                <div
                                                    draggable
                                                    onDragStart={() => setCompDragIdx(index)}
                                                    onDragEnd={() => setCompDragIdx(null)}
                                                    title="Sürüşdürüb sırala"
                                                    className="col-span-1 flex h-full cursor-move items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 select-none">
                                                    ⠿
                                                </div>
                                                <SearchSelect
                                                    value={item.menu_item_id}
                                                    onChange={(val) => updateItemRow(index, {
                                                        menu_item_id: val,
                                                        qty: item.qty || '1'
                                                    })}
                                                    options={itemSelectOptions}
                                                    placeholder="🔍 Məhsul seçin / axtarın..."
                                                    className="col-span-4"
                                                />
                                                <input
                                                    type="number"
                                                    min={saleType === 'piece' && item.unit_mode !== 'pors' ? '1' : '0.001'}
                                                    step={saleType === 'piece' && item.unit_mode !== 'pors' ? '1' : '0.001'}
                                                    value={item.qty}
                                                    onChange={(e) => updateItemRow(index, { qty: e.target.value })}
                                                    className="col-span-3 px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all"
                                                    placeholder="Miqdar"
                                                    required
                                                />
                                                {saleType === 'piece' ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleUnitMode(index)}
                                                        title="Vahidi dəyiş (ədəd / porsiya)"
                                                        className={`col-span-2 px-1 py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${
                                                            item.unit_mode === 'pors'
                                                                ? 'border-amber-300 bg-amber-50 text-amber-700'
                                                                : 'border-primary-300 bg-primary-50 text-primary-700'
                                                        }`}
                                                    >
                                                        {item.unit_mode === 'pors' ? 'pors' : 'əd'} ⇄
                                                    </button>
                                                ) : (
                                                    <div className="col-span-2 flex items-center justify-center text-xs font-bold text-gray-400">
                                                        {getSaleTypeLabel(saleType)}
                                                    </div>
                                                )}
                                                <div className="col-span-2 flex items-center justify-end gap-0.5">
                                                    <button type="button" onClick={() => moveItemRow(index, -1)} disabled={index === 0}
                                                        title="Yuxarı"
                                                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-all">▲</button>
                                                    <button type="button" onClick={() => moveItemRow(index, 1)} disabled={index === form.items.length - 1}
                                                        title="Aşağı"
                                                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-all">▼</button>
                                                    <button type="button" onClick={() => removeItemRow(index)}
                                                        title="Sil"
                                                        className="w-7 h-7 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-all">×</button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex gap-3 mt-2">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                >
                                    Ləğv et
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                >
                                    {editingSet ? 'Yadda saxla' : 'Əlavə et'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── WhatsApp göndər (set — şəkilli, texkarta ilə eyni) ── */}
            {waSet && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                     onClick={(e) => e.target === e.currentTarget && setWaSet(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <h3 className="font-bold text-gray-800">📱 WhatsApp-a göndər</h3>
                            <p className="text-xs text-gray-400 mt-0.5">🎁 {waSet.name} — set</p>
                        </div>
                        <div className="p-5 space-y-3">
                            <div>
                                <label className="text-[11px] font-semibold text-gray-500">Nömrə</label>
                                <input
                                    type="tel"
                                    value={waNum}
                                    onChange={(e) => setWaNum(e.target.value)}
                                    placeholder="Nömrə daxil edin"
                                    autoFocus
                                    className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:border-green-400"
                                />
                            </div>

                            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2.5 text-sm font-semibold text-amber-800">
                                <input type="checkbox" checked={waWithPrices}
                                    onChange={(e) => setWaWithPrices(e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-amber-600" />
                                💰 Alış / satış qiymətləri ilə göndər
                            </label>

                            {waHistory.length > 0 && (
                                <div>
                                    <p className="text-[11px] font-semibold text-gray-500 mb-1.5">Son göndərilənlər</p>
                                    <div className="flex flex-wrap gap-2">
                                        {waHistory.map((n) => (
                                            <button key={n} type="button" onClick={() => setWaNum(n)}
                                                className={`px-3 py-1.5 rounded-full text-xs font-mono font-semibold border transition-all ${
                                                    waNum === n
                                                        ? 'bg-green-600 text-white border-green-600'
                                                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-green-50 hover:border-green-300'
                                                }`}>
                                                📲 {n}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {waSet.image_url && (
                                <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-100 px-2.5 py-2">
                                    <img src={setImageUrl(waSet)} alt=""
                                        className="h-10 w-10 rounded object-cover border border-green-200"
                                        onError={(e) => { e.target.style.display = 'none'; }} />
                                    <span className="text-[11px] text-green-700">Şəkillə göndəriləcək (caption = set)</span>
                                </div>
                            )}

                            <div className="rounded-lg bg-gray-50 border border-gray-100 p-2.5 text-[11px] text-gray-500 whitespace-pre-wrap max-h-32 overflow-y-auto font-mono">
                                {buildSetMessage(waSet, waWithPrices)}
                            </div>
                        </div>
                        <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
                            <button onClick={() => setWaSet(null)}
                                className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-gray-500 border border-gray-200 hover:bg-gray-50">
                                Bağla
                            </button>
                            <button onClick={sendSetWhatsapp} disabled={waSending}
                                className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">
                                {waSending ? 'Göndərilir...' : '📤 Göndər'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const getSaleTypeLabel = (saleType) => {
    if (saleType === 'weight') return 'kq';
    if (saleType === 'liter') return 'lt';
    return 'əd';
};

export default MenuSetManager;
